import React from 'react';
import type { GamificationPeriod, GamificationRankingScope, GamificationVisibility } from '@troia-v3/types';

import { RadioGroup } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { SettingsSection } from './SettingsSection';

const PERIODS: Array<{ id: GamificationPeriod; label: string }> = [{ id: 'week', label: 'Semana' }, { id: 'month', label: 'Mês' }, { id: 'quarter', label: 'Trimestre' }];
export const SCOPE_LABELS: Record<GamificationRankingScope, string> = {
  users: 'Vendedores da empresa', users_by_team: 'Vendedores por equipe', users_by_unit: 'Vendedores por unidade', teams: 'Ranking de equipes', units: 'Ranking de unidades',
};
const SCOPES = Object.keys(SCOPE_LABELS) as GamificationRankingScope[];

interface Props {
  period: GamificationPeriod; rankings: GamificationRankingScope[]; visibility: GamificationVisibility;
  onChange: (p: { period?: GamificationPeriod; rankings?: GamificationRankingScope[]; visibility?: GamificationVisibility }) => void;
  disabled?: boolean;
}

export const RankingSection: React.FC<Props> = ({ period, rankings, visibility, onChange, disabled = false }) => {
  const toggle = (s: GamificationRankingScope) => onChange({ rankings: rankings.includes(s) ? rankings.filter((x) => x !== s) : [...rankings, s] });
  return (
    <SettingsSection icon="users" title="Ranking" description="Período de apuração e quais recortes ficam disponíveis. Vários podem estar ligados ao mesmo tempo — é a mesma pontuação somada de jeitos diferentes." testId="gamification-section-ranking">
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>Período (zera no início de cada)</label>
        <div className="gm-pills" role="radiogroup" aria-label="Período">
        {PERIODS.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={period === p.id} className={`gm-pill ${period === p.id ? 'gm-pill--on' : ''}`} onClick={() => onChange({ period: p.id })} disabled={disabled} data-testid={`gamification-period-${p.id}`}>{p.label}</button>
        ))}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>Recortes disponíveis</label>
        <div className="gm-pills">
        {SCOPES.map((s) => {
          const on = rankings.includes(s);
          return (
            <button key={s} type="button" role="checkbox" aria-checked={on} className={`gm-pill ${on ? 'gm-pill--on' : ''}`} onClick={() => toggle(s)} disabled={disabled} data-testid={`gamification-scope-${s}`}>
              <span className="bx"><Icon name="check" size={11} /></span>{SCOPE_LABELS[s]}
            </button>
          );
        })}
        </div>
        {rankings.length === 0 && <div className="text-xs mt-2" style={{ color: 'var(--theme-error-fg)' }} data-testid="gamification-rankings-error">Ligue pelo menos um recorte</div>}
      </div>
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-card-text)' }}>O que o vendedor enxerga</label>
        <RadioGroup
        variant="cards"
        value={visibility}
        onChange={(v) => onChange({ visibility: v as GamificationVisibility })}
        options={[
          { value: 'full', label: 'Ranking completo', description: 'Nomes, fotos e pontos de todos no recorte — como no app atual da Mastercell.', disabled },
          { value: 'top3_and_me', label: 'Pódio + a própria posição', description: 'Só os 3 primeiros aparecem com nome; cada um vê a própria posição e quanto falta para subir.', disabled },
          { value: 'only_me', label: 'Só os próprios números', description: 'Sem posição nem comparação. Pontos e nível continuam contando.', disabled },
        ]}
        data-testid="gamification-visibility"
        />
      </div>
    </SettingsSection>
  );
};
RankingSection.displayName = 'RankingSection';
